import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, BookOpen, Brush, ArrowLeft } from "lucide-react";
import StudioShell from "../components/studio/StudioShell";
import MandalaActivity from "../components/studio/MandalaActivity";
import { useStamp, StampToast } from "../components/studio/Passport";
import { MANDALA_FACTS, MANDALA_MOTIFS, MANDALA_PALETTE } from "../data/mandala";

const STEPS = [
  { id: "learn", label: "1. Learn", icon: BookOpen },
  { id: "build", label: "2. Create", icon: Brush },
];

// ---------- A read-only "sample" mandala for the Learn page ----------
function SampleMandala({ rings, bg = "#0A0F22" }) {
  return (
    <svg viewBox="0 0 200 200" className="w-full h-auto">
      <rect width="100%" height="100%" fill={bg} />
      <g transform="translate(100,100)">
        <circle cx="0" cy="0" r="2.5" fill={MANDALA_PALETTE[0].value} />
        {rings.map((ring, idx) => {
          const motif = MANDALA_MOTIFS.find((m) => m.id === ring.motifId);
          if (!motif) return null;
          if (motif.continuous) {
            return (
              <g key={idx}>
                {motif.render({ r: ring.r, color: ring.color, thickness: ring.thickness })}
              </g>
            );
          }
          return Array.from({ length: ring.segments }).map((_, i) => (
            <g key={`${idx}-${i}`} transform={`rotate(${(360 * i) / ring.segments})`}>
              {motif.render({ r: ring.r, color: ring.color, thickness: ring.thickness })}
            </g>
          ));
        })}
      </g>
    </svg>
  );
}

export default function MandalaStudio() {
  const [step, setStep] = useState("learn");
  const { earn, showToast, dismiss } = useStamp("mandala");

  return (
    <StudioShell>
      {/* Hero */}
      <section className="relative overflow-hidden pt-12 pb-8 sm:pt-16 sm:pb-12">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 relative text-center">
          <span className="inline-flex items-center gap-2 text-[#6A4FB6]">
            <Sparkles size={18} />
            <span className="overline !text-[#6A4FB6]">Module 4 · Across cultures</span>
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
            Build a
            <br />
            <em className="italic font-light">mandala, ring by ring.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
            A mandala grows like a slow ripple — from a single dot at the centre, outwards
            in perfect symmetry. Pick a motif, choose a colour, and add one ring at a time.
          </p>
        </div>
      </section>

      <nav className="max-w-6xl mx-auto px-4 sm:px-10" data-testid="mandala-steps">
        <div className="grid grid-cols-2 gap-2 sm:gap-4 p-2 sm:p-3 rounded-full bg-white border border-[#E5DFD3] shadow-sm">
          {STEPS.map((s) => {
            const Icon = s.icon;
            const active = step === s.id;
            return (
              <button
                key={s.id}
                data-testid={`mandala-step-${s.id}`}
                onClick={() => setStep(s.id)}
                className={`flex items-center justify-center gap-2 px-3 sm:px-5 py-3 rounded-full text-xs sm:text-sm tracking-wide transition-all duration-500 ${
                  active ? "bg-[#6A4FB6] text-[#F9F6F0]" : "text-[#5C6A8F] hover:text-[#1E2A4F]"
                }`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.label.split(". ")[1]}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-10 sm:py-16 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
          >
            {step === "learn" && <MandalaLearn />}
            {step === "build" && (
              <>
                <div className="text-center mb-10">
                  <h2 className="font-display text-4xl sm:text-5xl text-[#1E2A4F] leading-tight">
                    Grow your mandala.
                  </h2>
                  <p className="mt-3 text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
                    Each ring is its own little choice — a motif, a colour, a number of
                    segments. Watch your faint ghost ring at the edge to preview what comes
                    next, then commit it with <strong>Add ring</strong>.
                  </p>
                </div>
                <MandalaActivity onSave={earn} />
              </>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex items-center justify-between flex-wrap gap-3">
          {step === "build" && (
            <button
              data-testid="mandala-prev"
              onClick={() => setStep("learn")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
            >
              <ArrowLeft size={14} /> Previous
            </button>
          )}
          {step === "learn" && (
            <button
              data-testid="mandala-next"
              onClick={() => setStep("build")}
              className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#6A4FB6] text-[#F9F6F0] hover:bg-[#1E2A4F] transition-colors duration-300 text-sm font-medium tracking-wide"
            >
              Start creating →
            </button>
          )}
        </div>
      </section>

      <StampToast show={showToast} artName="Mandala" onDismiss={dismiss} />
    </StudioShell>
  );
}

function MandalaLearn() {
  const sampleA = [
    { motifId: "dot", color: "#E0A82E", segments: 8, thickness: 3, r: 14 },
    { motifId: "petal", color: "#E54B6A", segments: 8, thickness: 4, r: 32 },
    { motifId: "ring-outline", color: "#F9F6F0", segments: 1, thickness: 2, r: 50 },
    { motifId: "diamond", color: "#2DB7A1", segments: 16, thickness: 4, r: 68 },
    { motifId: "lotus", color: "#9B59B6", segments: 12, thickness: 5, r: 88 },
  ];
  const sampleB = [
    { motifId: "ring", color: "#F49B0B", segments: 1, thickness: 2, r: 18 },
    { motifId: "triangle-out", color: "#C03546", segments: 12, thickness: 3, r: 36 },
    { motifId: "spoke", color: "#F9F6F0", segments: 24, thickness: 2, r: 58 },
    { motifId: "leaf", color: "#2E5E3E", segments: 12, thickness: 4, r: 82 },
  ];
  const sampleC = [
    { motifId: "dot", color: "#F9F6F0", segments: 12, thickness: 3, r: 18 },
    { motifId: "petal", color: "#1F76A8", segments: 12, thickness: 4, r: 38 },
    { motifId: "ring-outline", color: "#E0A82E", segments: 1, thickness: 2, r: 56 },
    { motifId: "triangle-in", color: "#E54B6A", segments: 24, thickness: 3, r: 74 },
    { motifId: "lotus", color: "#F49B0B", segments: 16, thickness: 5, r: 92 },
  ];

  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="font-display text-4xl sm:text-5xl text-[#1E2A4F] leading-tight">
          The story of the mandala.
        </h2>
        <p className="mt-3 text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
          A drawing meditation — a ripple of repetition that quiets the mind.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-10">
        {MANDALA_FACTS.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`mandala-fact-${i}`}
            className="p-6 sm:p-7 rounded-2xl bg-white border border-[#E5DFD3] hover:border-[#6A4FB6] transition-colors duration-500"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#EFEAFA] text-2xl">
              {f.emoji}
            </span>
            <h3 className="font-display text-2xl text-[#1E2A4F] mt-4">{f.title}</h3>
            <p className="text-[15px] text-[#5C6A8F] leading-relaxed mt-2">{f.body}</p>
          </motion.div>
        ))}
      </div>

      {/* Sample showcase */}
      <div className="p-6 sm:p-10 rounded-2xl bg-[#1E2A4F]" data-testid="mandala-sample">
        <p className="overline !text-[#E76F51] text-center">A glimpse of the art</p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { rings: sampleA, label: "Lotus", bg: "#0A0F22" },
            { rings: sampleB, label: "Sun wheel", bg: "#3B1F4A" },
            { rings: sampleC, label: "Star bloom", bg: "#1E2A4F" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-xl overflow-hidden border border-[#F9F6F0]/10"
            >
              <SampleMandala rings={s.rings} bg={s.bg} />
              <p className="text-center text-xs tracking-[0.2em] uppercase text-[#F9F6F0]/70 py-3 bg-[#0A0F22]">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-[#6A4FB6] text-[#F9F6F0] text-center">
        <p className="overline !text-[#FFE08A]">A note from Meera</p>
        <p className="font-display italic text-xl sm:text-2xl mt-3 leading-snug max-w-2xl mx-auto">
          &ldquo;The mandala teaches you that beauty doesn&apos;t need to be in a hurry —
          one ring at a time, one slow breath at a time.&rdquo;
        </p>
      </div>
    </div>
  );
}
